import { useState } from 'react'

import type { Filter } from '../../../../generated/frontend'
import type {
  NBFilterCallback,
  NeedsBasedAnswerOptionExtended,
  NeedsBasedFiltersOptionsMap,
  PreviousNeedsBasedFilterLabels,
} from '../../../../shared/types'
import { get } from '../../../../shared/utils/get'
import { set } from '../../../../shared/utils/set'
import { getFilterAttributeValue } from '../components/Filter/utils'
import { updateFilter } from '../components/Filters/utils'
import { findCommonBasicFilter } from '../utils/find-common-basic-filter'
import { getDropdownPathsAndUpdatedFilters } from '../utils/get-dropdown-paths-and-updated-filters'

export const useNeedsBasedFilters = ({
  filterDataMap,
  filtersToRemoveMap,
  isOverlayFiltersDisplayed,
  needsBasedLabelsFromQueryParams,
  needsBasedOptionsFromQueryParams,
  setFiltersToRemoveMap,
}) => {
  const [previousNeedsBasedFilterLabels, setPreviousNeedsBasedFilterLabels] =
    useState<PreviousNeedsBasedFilterLabels>(needsBasedLabelsFromQueryParams)

  const [needsBasedFiltersOptionsMap, setNeedsBasedFiltersOptionsMap] =
    useState<NeedsBasedFiltersOptionsMap>({
      currentOptions: needsBasedOptionsFromQueryParams,
      previousOptions: needsBasedOptionsFromQueryParams,
    })

  const handleNeedsBasedFilters = ({
    options,
    questionText,
    label,
    individualFilters,
    isSelected,
  }: NBFilterCallback) => {
    const previousBucketLabel = previousNeedsBasedFilterLabels[questionText]
    const {
      basic: clonedBasicFilterData,
      needsBased: clonedNeedsBasedFilterData,
      needsBasedQueryValues: clonedNbfForQueryValues,
    } = structuredClone(filterDataMap)
    const { basic: newNbFiltersToRemove, needsBasedQueryValues: newNbfQueryValuesToRemove } =
      structuredClone(filtersToRemoveMap)

    const { previousOptions } = needsBasedFiltersOptionsMap

    const updateFilterDataCallback = ({
      option,
      commonBasicFilter,
      removeFilterValue,
      addFilterValue,
      bucketLabel,
    }: {
      option: NeedsBasedAnswerOptionExtended
      commonBasicFilter?: Filter
      bucketLabel: string
      removeFilterValue?: boolean
      addFilterValue?: boolean
    }) => {
      if (commonBasicFilter?.componentType === 'dropdown') {
        const { toPath, fromPath, updatedFilterTo, updatedFilterFrom } =
          getDropdownPathsAndUpdatedFilters({
            filterData: clonedBasicFilterData,
            option,
            removeFilterValue,
            addFilterValue,
          })
        if (isOverlayFiltersDisplayed) {
          if (option?.to !== null) {
            set(newNbFiltersToRemove, `${toPath}`, [])
          }
          if (option?.from !== null) {
            set(newNbFiltersToRemove, `${fromPath}`, [])
          }
        }
        if (option?.to !== null) {
          set(clonedBasicFilterData, toPath, updatedFilterTo)
        }
        if (option?.from !== null) {
          set(clonedBasicFilterData, fromPath, updatedFilterFrom)
        }
      } else {
        const filterAttributeValue = getFilterAttributeValue({
          valueBool: option?.valueBool,
          valueStr: option?.valueStr,
          from: option?.from,
          to: option?.to,
        })

        const path = `${option.externalId}.${option.code}`
        const filterDataToUpdate = commonBasicFilter
          ? clonedBasicFilterData
          : clonedNeedsBasedFilterData
        const currentFilter = get(filterDataToUpdate, path, []) as string[]
        const updatedFilter = updateFilter({
          filter: currentFilter,
          filterValue: filterAttributeValue,
          removeFilterValue,
          addFilterValue,
        })

        if (!commonBasicFilter) {
          set(
            clonedNbfForQueryValues,
            `${bucketLabel}.${option.externalId}.${option.code}`,
            updatedFilter
          )
          if (isOverlayFiltersDisplayed) {
            set(newNbfQueryValuesToRemove, `${bucketLabel}.${option.externalId}.${option.code}`, [])
          }
        } else if (isOverlayFiltersDisplayed) {
          set(newNbFiltersToRemove, `${option.externalId}.${option.code}`, [])
        }

        set(filterDataToUpdate, path, updatedFilter)
      }
    }
    /* v8 ignore else */
    if (previousOptions?.[questionText]?.length) {
      findCommonBasicFilter({
        filterOptions: previousOptions?.[questionText],
        basicFilters: individualFilters,
        onOptionCallback: ({ commonBasicFilter, option }) => {
          updateFilterDataCallback({
            commonBasicFilter,
            bucketLabel: previousBucketLabel,
            option,
            removeFilterValue: true,
          })
        },
      })
    }
    /* v8 ignore else */
    if (!isSelected) {
      findCommonBasicFilter({
        filterOptions: options,
        basicFilters: individualFilters,
        onOptionCallback: ({ commonBasicFilter, option }) => {
          updateFilterDataCallback({
            commonBasicFilter,
            bucketLabel: label,
            option,
            addFilterValue: true,
          })
        },
      })
    }

    setFiltersToRemoveMap({
      ...filtersToRemoveMap,
      basic: newNbFiltersToRemove,
      needsBasedQueryValues: newNbfQueryValuesToRemove,
    })

    setNeedsBasedFiltersOptionsMap({
      previousOptions: {
        ...needsBasedFiltersOptionsMap.previousOptions,
        [questionText]: isSelected ? /* v8 ignore next */ [] : options,
      },
      currentOptions: {
        ...needsBasedFiltersOptionsMap.currentOptions,
        [questionText]: options,
      },
    })

    setPreviousNeedsBasedFilterLabels({
      ...previousNeedsBasedFilterLabels,
      [questionText]: label,
    })

    return {
      updatedNeedsBasedFilterData: clonedNeedsBasedFilterData,
      updatedBasicFilterData: clonedBasicFilterData,
      updatedNbfForQueryValues: clonedNbfForQueryValues,
    }
  }

  return {
    handleNeedsBasedFilters,
    setPreviousNeedsBasedFilterLabels,
    needsBasedFiltersOptionsMap,
    setNeedsBasedFiltersOptionsMap,
  }
}
